/**
 * TaskHubSocialPosts.jsx — Phase 153, extended Phase 154. The "Social
 * Posts" tab inside the Home Task Hub. A thin, high-speed surface over
 * `/marketing/drafts` with the SAME row layout/fields as the Tasks tab:
 * Title, Project, Assignee, Importance, Start Date, Due("Scheduled for")
 * Date, Log Hours, brief/side-panel icon, delete. Deeper editing
 * (channels, checklist, per-platform copy, images) still happens in the
 * full Plan Board — each row links out to it.
 *
 * New entries default to the "Marketing & Growth" project (Phase 153) but
 * the Project field can be freely overridden, same as any Tasks-tab row.
 *
 * Lifecycle: Idea → In Progress → Review → Scheduled → Published. Only a
 * Founder or a post's own project Lead may move it to Scheduled or
 * Published — those two options are disabled in the dropdown for everyone
 * else, and the backend (`_user_can_schedule_social_post`) enforces the
 * same rule regardless.
 *
 * Log Hours / Timesheet sync: clicking the Log Hours cell POSTs
 * `/marketing/drafts/{id}/log-time`, which creates a real Timesheet entry
 * for the post's owner + project and bumps `total_minutes`.
 *
 * Date Sync: setting "Scheduled for" here PATCHes the same `scheduled_for`
 * field Plan Board / the Marketing Calendar use — the backend's
 * `_sync_task_from_marketing_draft` mirrors it onto a linked daily task's
 * due date automatically, so no extra sync logic is needed here.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Loader2, Trash2, ExternalLink, Lock, FileText } from "lucide-react";
import CategorizedProjectSelect from "@/components/CategorizedProjectSelect";
import TaskBriefPanel from "@/components/TaskBriefPanel";
import TaskHubLogHours from "@/components/TaskHubLogHours";
import LinkedInApprovals from "@/components/LinkedInApprovals";

const SOCIAL_STATUSES = [
  { id: "idea", label: "Idea" },
  { id: "in_progress", label: "In Progress" },
  { id: "review", label: "Review" },
  { id: "scheduled", label: "Scheduled" },
  { id: "published", label: "Published" },
];
const STATUS_TONE = {
  idea: "border-slate-200 text-slate-600 bg-white",
  planned: "border-slate-200 text-slate-600 bg-white",
  in_progress: "border-blue-200 text-blue-700 bg-blue-50",
  review: "border-purple-200 text-purple-700 bg-purple-50",
  scheduled: "border-amber-200 text-amber-700 bg-amber-50",
  published: "border-emerald-200 text-emerald-700 bg-emerald-50",
};
const IMPORTANCE_TONE = {
  high: "border-red-300 text-red-700 bg-red-50",
  medium: "border-amber-300 text-amber-700 bg-amber-50",
  low: "border-slate-300 text-slate-600 bg-slate-50",
};
function normStatus(s) { return s === "planned" ? "idea" : (s || "idea"); }

export default function TaskHubSocialPosts({ team, statusFilter, reloadSignal }) {
  const { user } = useAuth();
  const can = usePermissions();
  const isFounder = user?.role === "founder";

  // Phase 153.1 — fetched directly (not derived from /daily-tasks/projects,
  // whose RBAC hides projects a caller isn't a member of) so the default
  // project + gating check work for anyone with Home access.
  const [marketingProject, setMarketingProject] = useState(null);
  useEffect(() => {
    api.get("/marketing/growth-project").then(({ data }) => setMarketingProject(data?.id ? data : null)).catch(() => setMarketingProject(null));
  }, []);

  // Phase 154 — field parity with Tasks: a real Project dropdown, not a
  // fixed scope. Falls back to the caller's own project list; Marketing &
  // Growth is always injected as an option even for staff not formally on
  // that team, since it's this tab's default.
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    api.get("/daily-tasks/projects").then(({ data }) => setProjects(data || [])).catch(() => setProjects([]));
  }, []);
  const projectOptions = useMemo(() => {
    if (!marketingProject) return projects;
    return projects.some((p) => p.id === marketingProject.id) ? projects : [marketingProject, ...projects];
  }, [projects, marketingProject]);

  const [drafts, setDrafts] = useState(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftProject, setDraftProject] = useState("");
  const [draftAssignee, setDraftAssignee] = useState("");
  const [draftImportance, setDraftImportance] = useState("medium");
  const [draftStartDate, setDraftStartDate] = useState("");
  const [draftDueDate, setDraftDueDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [briefDraft, setBriefDraft] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/marketing/drafts");
      setDrafts(data || []);
    } catch (e) { toast.error(formatApiError(e)); setDrafts([]); }
  }, []);
  useEffect(() => { load(); }, [load, reloadSignal]);

  const canScheduleFor = useCallback((projectId) => {
    if (isFounder || can("marketing_admin")) return true;
    const proj = projectOptions.find((p) => p.id === projectId) || marketingProject;
    if (!proj) return false;
    return (proj.team_members || []).some((m) => m.user_id === user?.id && m.project_role === "lead");
  }, [isFounder, can, projectOptions, marketingProject, user?.id]);

  // Phase 154 — same scope as the Tasks tab: every post regardless of
  // project (Project is now a real per-row field, not a fixed filter).
  const scoped = useMemo(() => {
    if (!drafts) return [];
    let rows = statusFilter === "done" ? drafts.filter((d) => d.status === "published") : drafts.filter((d) => d.status !== "published");
    return [...rows].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [drafts, statusFilter]);

  const create = async (openBrief = false) => {
    const title = draftTitle.trim();
    if (!title) return;
    const pid = draftProject || marketingProject?.id;
    if (!pid) { toast.error("Pick a project — every post must belong to one"); return; }
    if (!draftStartDate || !draftDueDate) { toast.error("Start date and Due date are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/marketing/drafts", {
        title, caption: title, channels: ["instagram"], project_id: pid,
        owner_id: draftAssignee || undefined,
        importance: draftImportance,
        start_date: draftStartDate,
        scheduled_for: `${draftDueDate}T09:00:00`,
        scheduled_tz: "Asia/Kolkata",
        source: "task_hub",
      });
      setDrafts((arr) => [data, ...(arr || [])]);
      setDraftTitle(""); setDraftProject(""); setDraftAssignee(""); setDraftImportance("medium"); setDraftStartDate(""); setDraftDueDate("");
      if (openBrief) setBriefDraft(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const patch = async (row, body) => {
    try {
      const { data } = await api.patch(`/marketing/drafts/${row.id}`, body);
      setDrafts((arr) => (arr || []).map((x) => (x.id === row.id ? data : x)));
      return data;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const logHours = async (row, hours) => {
    try {
      const { data } = await api.post(`/marketing/drafts/${row.id}/log-time`, { hours });
      setDrafts((arr) => (arr || []).map((x) => (x.id === row.id ? data.draft : x)));
      toast.success(`Logged ${hours}h → Timesheet updated`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title || row.caption}"?`)) return;
    try {
      await api.delete(`/marketing/drafts/${row.id}`);
      setDrafts((arr) => (arr || []).filter((x) => x.id !== row.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      {isFounder && <LinkedInApprovals />}
      {/* Quick add — same fields, order, and styling as the Tasks tab. */}
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-1.5 flex-wrap" data-testid="task-hub-social-quick-add">
        <Input
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); create(); } }}
          placeholder="Add a post idea…"
          className="h-8 rounded-sm text-sm flex-1 min-w-[140px]"
          data-testid="task-hub-social-quick-add-title"
        />
        <CategorizedProjectSelect
          projects={projectOptions}
          value={draftProject || marketingProject?.id || "none"}
          onChange={(v) => setDraftProject(v === "none" ? "" : v)}
          topOption={{ value: "none", label: "Project…" }}
          className="h-8 text-sm rounded-sm border border-slate-200 bg-white w-auto max-w-[170px]"
          testId="task-hub-social-quick-add-project"
        />
        <select
          value={draftAssignee}
          onChange={(e) => setDraftAssignee(e.target.value)}
          className="h-8 text-sm rounded-sm border border-slate-200 px-2 bg-white max-w-[130px]"
          data-testid="task-hub-social-quick-add-assignee"
          title="Owner (defaults to you)"
        >
          <option value="">Self</option>
          {(team || []).filter((u) => u.id !== user?.id).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
        <select
          value={draftImportance}
          onChange={(e) => setDraftImportance(e.target.value)}
          className="h-8 text-sm rounded-sm border border-slate-200 px-2 bg-white max-w-[100px]"
          data-testid="task-hub-social-quick-add-importance"
          title="Importance"
        >
          <option value="high">High</option>
          <option value="medium">Mid</option>
          <option value="low">Low</option>
        </select>
        <Input
          type="date"
          value={draftStartDate}
          onChange={(e) => setDraftStartDate(e.target.value)}
          className="h-8 rounded-sm text-sm max-w-[130px]"
          data-testid="task-hub-social-quick-add-start-date"
          title="Start date (required)"
        />
        <Input
          type="date"
          value={draftDueDate}
          onChange={(e) => setDraftDueDate(e.target.value)}
          className="h-8 rounded-sm text-sm max-w-[130px]"
          data-testid="task-hub-social-quick-add-due-date"
          title="Due date (required) — becomes 'Scheduled for'"
        />
        <Button
          size="sm"
          onClick={() => create()}
          disabled={saving || !draftTitle.trim()}
          className="rounded-sm h-8 bg-[#F54800] hover:bg-[#D63E00] text-white"
          data-testid="task-hub-social-quick-add-save"
        >
          {saving ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Plus size={12} className="mr-1" />} Save
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => create(true)}
          disabled={saving || !draftTitle.trim()}
          className="rounded-sm h-8 px-2"
          title="Save + write a detailed brief"
          data-testid="task-hub-social-quick-add-brief"
        >
          <FileText size={12} />
        </Button>
      </div>

      <div className="max-h-[440px] overflow-y-auto overflow-x-auto" data-testid="task-hub-social-rows">
        {drafts === null && <div className="px-4 py-6 text-xs text-slate-400 italic">Loading…</div>}
        {drafts && scoped.length === 0 && (
          <div className="px-4 py-6 text-xs text-slate-400 italic text-center" data-testid="task-hub-social-empty">
            Nothing here. Add one above, or turn an Inspiration idea into a post.
          </div>
        )}
        <div className="min-w-[760px]">
          {scoped.map((row) => (
            <SocialPostRow
              key={row.id}
              row={row}
              team={team}
              projects={projectOptions}
              canSchedule={canScheduleFor(row.project_id)}
              onPatch={patch}
              onLogHours={(h) => logHours(row, h)}
              onRemove={remove}
              onOpenBrief={() => setBriefDraft(row)}
            />
          ))}
        </div>
      </div>

      <TaskBriefPanel
        task={briefDraft}
        fieldValue={briefDraft?.notes}
        onOpenChange={(v) => { if (!v) setBriefDraft(null); }}
        onSave={(text) => api.patch(`/marketing/drafts/${briefDraft.id}`, { notes: text }).then((r) => r.data)}
        onSaved={(updated) => setDrafts((arr) => (arr || []).map((x) => (x.id === updated.id ? updated : x)))}
      />
    </>
  );
}

function SocialPostRow({ row, team, projects, canSchedule, onPatch, onLogHours, onRemove, onOpenBrief }) {
  const [title, setTitle] = useState(row.title || row.caption?.split("\n")[0] || "");
  useEffect(() => { setTitle(row.title || row.caption?.split("\n")[0] || ""); }, [row.title, row.caption]);
  const status = normStatus(row.status);
  const importance = row.importance || "medium";
  const dateStr = row.scheduled_for ? row.scheduled_for.slice(0, 10) : "";
  const hasBrief = !!(row.notes && row.notes.trim());

  const onDateChange = (v) => {
    if (!v) { onPatch(row, { scheduled_for: null }); return; }
    const time = row.scheduled_for && row.scheduled_for.includes("T") ? row.scheduled_for.split("T")[1] : "09:00:00";
    onPatch(row, { scheduled_for: `${v}T${time}`, scheduled_tz: row.scheduled_tz || "Asia/Kolkata" });
  };

  return (
    <div className="px-4 py-2 flex items-center gap-2 border-b border-slate-50 last:border-0 hover:bg-slate-50/60 group" data-testid={`task-hub-social-row-${row.id}`}>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={() => { if (title.trim() && title !== row.title) onPatch(row, { title: title.trim(), caption: row.caption || title.trim() }); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        className={`min-w-0 flex-1 text-sm bg-transparent border-none outline-none focus:ring-1 focus:ring-slate-300 rounded-sm px-1 ${status === "published" ? "line-through text-slate-400" : "text-slate-800"}`}
        data-testid={`task-hub-social-row-title-${row.id}`}
      />

      <button
        onClick={onOpenBrief}
        className={`shrink-0 p-1 rounded-sm ${hasBrief ? "text-[#F54800] hover:text-[#D63E00]" : "text-slate-300 hover:text-slate-500"}`}
        title={hasBrief ? "View / edit brief" : "Add a detailed brief"}
        data-testid={`task-hub-social-row-brief-${row.id}`}
      >
        <FileText size={13} />
      </button>

      <CategorizedProjectSelect
        projects={projects}
        value={row.project_id || "none"}
        onChange={(v) => onPatch(row, { project_id: v === "none" ? null : v })}
        topOption={{ value: "none", label: "No project" }}
        className="h-7 text-[11px] rounded-sm border border-slate-200 bg-white w-[130px] shrink-0"
        testId={`task-hub-social-row-project-${row.id}`}
        keepProjectId={row.project_id}
      />

      <select
        value={row.owner_id || ""}
        onChange={(e) => onPatch(row, { owner_id: e.target.value || null })}
        className="h-7 text-[11px] rounded-sm border border-slate-200 bg-white w-[120px] shrink-0"
        data-testid={`task-hub-social-row-owner-${row.id}`}
        title="Owner"
      >
        <option value="">Unassigned</option>
        {row.owner_id && !(team || []).find((u) => u.id === row.owner_id) && (
          <option value={row.owner_id}>{row.owner_name || "Current"}</option>
        )}
        {(team || []).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
      </select>

      <div className="flex items-center gap-1 shrink-0">
        <select
          value={status}
          onChange={(e) => onPatch(row, { status: e.target.value })}
          className={`h-7 text-[11px] rounded-sm border px-1.5 font-medium w-[110px] ${STATUS_TONE[status] || STATUS_TONE.idea}`}
          data-testid={`task-hub-social-row-status-${row.id}`}
        >
          {SOCIAL_STATUSES.map((s) => (
            <option key={s.id} value={s.id} disabled={!canSchedule && (s.id === "scheduled" || s.id === "published")}>
              {s.label}
            </option>
          ))}
        </select>
        {!canSchedule && (
          <Lock size={10} className="text-slate-300" title="Only a Founder or the project Lead can schedule or publish" />
        )}
      </div>

      <select
        value={importance}
        onChange={(e) => onPatch(row, { importance: e.target.value })}
        className={`h-7 text-[10px] rounded-sm border px-1 font-medium w-[68px] shrink-0 ${IMPORTANCE_TONE[importance]}`}
        data-testid={`task-hub-social-row-importance-${row.id}`}
        title="Importance"
      >
        <option value="high">High</option>
        <option value="medium">Mid</option>
        <option value="low">Low</option>
      </select>

      <input
        type="date"
        value={row.start_date ? row.start_date.slice(0, 10) : ""}
        onChange={(e) => onPatch(row, { start_date: e.target.value })}
        className="h-7 text-[11px] rounded-sm border border-slate-200 bg-white px-1 w-[128px] shrink-0"
        data-testid={`task-hub-social-row-start-date-${row.id}`}
        title="Start date"
      />

      <input
        type="date"
        value={dateStr}
        onChange={(e) => onDateChange(e.target.value)}
        className="h-7 text-[11px] rounded-sm border border-slate-200 bg-white px-1 w-[128px] shrink-0"
        data-testid={`task-hub-social-row-date-${row.id}`}
        title="Due date (Scheduled for)"
      />

      <TaskHubLogHours
        totalMinutes={row.total_minutes || 0}
        onLog={onLogHours}
        testId={`task-hub-social-row-hours-${row.id}`}
      />

      {row.project_id && (
        <Link to={`/projects/${row.project_id}`} aria-label="Open in Marketing" className="shrink-0" title="Open full editor in Marketing">
          <ExternalLink size={11} className="text-slate-300 hover:text-slate-500" />
        </Link>
      )}

      <button onClick={() => onRemove(row)} className="text-slate-300 hover:text-rose-600 p-1 shrink-0" data-testid={`task-hub-social-row-delete-${row.id}`} title="Delete">
        <Trash2 size={12} />
      </button>
    </div>
  );
}
